/* eslint-disable max-lines-per-function */
import { useState, useMemo } from 'react'
import { GetServerSidePropsContext } from 'next'
import isObject from 'lodash/isObject'
import isEmpty from 'lodash/isEmpty'
// import { queryClient } from '@/stencil/np-payment/add-bank'
import { QueryClient, useQuery } from 'react-query'

import {
  IN_STOCKS_CATELOGUE,
  indianStocksApiBaseUrl,
  indianStocksBackgroundApiBaseUrl,
} from '@/root/api/apiConstants'
import SomethingWentWrong from '@/components/Common/SomethingWentWrong'
import useGetINStockLivePriceFromSocket from '@/components/Stocks/LivePrices/useGetINStockLivePriceFromSocket'
import { IHeaders, TLivePrice } from '@/components/Stocks/Pages/StockDetail'
import { TAssetDetailsData } from '@/components/Stocks/Widgets/types'
import { DOMAIN } from '@/root/constants'
import BreadCrumb from '@/stories/components/Common/BreadCrumbs'
import SEOHeader from '@/components/Common/SEOHeader'
import { fetchRequest } from '@/root/utils/network-adapter/fetch-request'
import { getInitData } from '@/root/utils/network-adapter/useQuery-helpers'
import Link from '@/components/Common/Link'
import { INDSTOCKS_HOST } from '@/root/constants/common'
import FullScreenLoader from '@/stories/components/Common/FullScreenLoader'
import HeaderSection from '@/components/Stocks/technical-analysis/HeaderSection'
import TimelineTabs from '@/components/Stocks/technical-analysis/Timeline'
import OuterWidget from '@/components/Common/OuterWidget/OuterWidget'
import OverallSummaryTable from '@/components/Stocks/technical-analysis/OverallTable'
import Table from '@/components/Stocks/technical-analysis/Table'
import GaugeWidget from '@/components/Stocks/technical-analysis/GaugeWidget'

type TStockDetailErrorObj = {
  debug_info: string
  message: string
  statusCode: number
}

function isStocksDetailsErrorObject(arg: unknown): arg is TStockDetailErrorObj {
  if (isObject(arg)) {
    if ('statusCode' in arg) {
      return true
    }

    return false
  }
  return false
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 3,
      cacheTime: Infinity,
      staleTime: Infinity,
    },
  },
})

export default function StocksTechnicalAnalysis({
  apiResponse,
  custHeader,
  headers,
  errorStatusCode,
  member_id,
  slug,
}: {
  apiResponse?: TAssetDetailsData
  errorStatusCode: number
  custHeader: boolean
  headers: IHeaders
  member_id: string | null
  slug
}) {
  const [livePrice, setLivePrice] = useState<TLivePrice | null>(null)

  const {
    data: response,
    isError,
    isLoading,
  } = useQuery<TAssetDetailsData>(
    `indianstocksTechDetails${slug}`,
    () =>
      fetchRequest({
        config: {
          headers,
        },
        timeout: 0,
        url: `${
          custHeader ? indianStocksBackgroundApiBaseUrl : indianStocksApiBaseUrl
        }${IN_STOCKS_CATELOGUE}${slug}?response_format=json&catalog-required=true&graph-optional=true&investment-optional=true&sub-tab-id=3${
          member_id ? `&member_id=${member_id}` : ''
        }`,
      }),
    {
      retry: 0,
      staleTime: 300,
      enabled: errorStatusCode !== 200 || !apiResponse,
      ...getInitData(errorStatusCode === 200 && apiResponse ? apiResponse : {}),
    },
  )

  const { catalog = {} } = response ?? {}
  const { stock_details, entity_basic = {}, entity_stats = {} } = catalog
  const { live_price, day_change_percentage } = entity_stats
  const {
    logo = '',
    name = '',
    socket_path = '',
    symbol = '',
    relativepath = '',
  } = entity_basic
  const technical_analysis = useMemo(
    () => stock_details?.technical_analysis ?? [],
    [stock_details?.technical_analysis],
  )
  useGetINStockLivePriceFromSocket(socket_path, setLivePrice)

  const [selectedTab, setSelectedTab] = useState<number>(() =>
    technical_analysis.length ? 0 : -1,
  )

  if (isLoading) {
    return <FullScreenLoader />
  }

  if (isError) {
    return <SomethingWentWrong />
  }

  const seoData = {
    title: `${name} Technicals Summary : RSI, MACD, Stochastic, DMA, EMA, SMA`,
    description: `Get detailed technical analysis of ${name} share with insights through Moving Averages and Oscillators like SMA, EMA, RSI, MACD, and more for informed decisions with INDmoney`,
    canonicalUrl: `${DOMAIN}${stock_details?.ta_path}`,
  }

  if (!technical_analysis.length) {
    return (
      <>
        {' '}
        <SEOHeader data={seoData} />
        <div className="flex h-screen flex-col items-center justify-center">
          <p className="text-brand-red-cinnabar flex ">
            No Technical Analysis available
          </p>
          <Link
            className="btn bg-brand-green-dark my-4  flex px-6  py-4 text-base font-medium lg:text-lg"
            href={relativepath}
          >
            Explore Stocks
          </Link>
        </div>
      </>
    )
  }

  const data = technical_analysis[selectedTab] ?? {}
  const { moving_average, oscillation, summary } = data

  const breadCrumbList = () => [
    { label: 'Home', link: '/' },
    { label: 'Stocks', link: '/stocks' },
    { label: name, link: `${relativepath}` },
    { label: `${name} Technicals` },
  ]

  const livePriceNumber = livePrice?.price ?? live_price
  const dayChangePerc = livePrice?.percent_change ?? day_change_percentage
  return (
    <div>
      <SEOHeader data={seoData} />
      <div className="relative mx-auto mb-36 max-w-3xl px-2 py-6">
        <BreadCrumb breadCrumbList={breadCrumbList()} />
        <HeaderSection
          logo={logo}
          name={name}
          symbol={symbol}
          livePriceNumber={livePriceNumber}
          dayChangePerc={dayChangePerc}
        />

        {/* tabs */}
        <TimelineTabs
          technical_analysis={technical_analysis}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
        />

        <div className="mt-4 flex flex-col gap-20">
          <GaugeWidget
            summary={[
              { ...summary, name: 'Technicals Summary' },
              { ...moving_average?.summary, name: 'Moving Average' },
              { ...oscillation?.summary, name: 'Oscillation' },
            ]}
          />
          {!!summary?.trends?.length && (
            <OverallSummaryTable trends={summary.trends} />
          )}
          {oscillation?.indicators?.length && (
            <OuterWidget title="Oscillators">
              <Table indicators={oscillation.indicators} />
            </OuterWidget>
          )}

          {!!moving_average?.indicators?.length && (
            <OuterWidget title="Moving Averages">
              <Table indicators={moving_average.indicators} />
            </OuterWidget>
          )}
        </div>

        <p className="text-brand-grey-base mt-4 text-xs">
          *Disclaimer: The content present here is for informational purposes
          only, you should not construe any such information or other material
          as investment advisory.
        </p>
      </div>
    </div>
  )
}

export const getServerSideProps = async (
  context: GetServerSidePropsContext,
) => {
  let slug = context.params?.stockSlug as string
  slug = slug.replace('share-price', 'technical-analysis')
  const custHeader = context.req.headers['x-isb-background']
  const token = context.query.token ?? context.req.cookies.token ?? null

  const headers = {
    ...(token && { Authorization: `Bearer ${token}` }),
    Platform: 'web',
  }

  const member_id =
    (context.query.memberId ? Number(context.query.memberId) : null) ??
    (context.req.cookies.memberId
      ? Number(context.req.cookies.memberId)
      : null) ??
    null

  // change ticker to `slug` before merging
  const apiUrl = `${
    custHeader ? indianStocksBackgroundApiBaseUrl : indianStocksApiBaseUrl
  }${IN_STOCKS_CATELOGUE}${slug}?response_format=json&catalog-required=true&graph-optional=true&investment-optional=true&sub-tab-id=3${
    member_id ? `&member_id=${member_id}` : ''
  }`
  let errorStatusCode: number | undefined
  const data = await queryClient.fetchQuery<TAssetDetailsData>(
    `indianstocksTechDetails${slug}`,
    async () => {
      let data

      try {
        data = await fetchRequest({
          config: {
            headers: {},
          },
          timeout: 1000,
          url: apiUrl,
        })
      } catch (e: unknown) {
        if (isStocksDetailsErrorObject(e)) {
          errorStatusCode = e.statusCode
          data = e
        } else {
          errorStatusCode = 404
        }
        //throw new Error('Stock detail page error')
      }
      return data ?? {}
    },
    {
      retry: 0,
      staleTime: 300,
    },
  )

  if (context.req.headers.host === INDSTOCKS_HOST || errorStatusCode) {
    if (errorStatusCode) {
      context.res.statusCode = errorStatusCode
    }
    context.res.setHeader('X-Middleware-Caching', 'false')
  }

  if (!isEmpty(data.catalog) && !token) {
    context.res.setHeader(
      'Cache-Control',
      'public, s-maxage=43200', // 12 hours cloudflare cache
    )
  }

  return {
    props: {
      apiResponse: data,
      custHeader: !!custHeader,
      headers,
      errorStatusCode: errorStatusCode ?? 200,
      member_id,
      slug,
    },
  }
}
